import { useState, useEffect, useMemo } from "react";
import { ListenerLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, Trash2, X, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" }
];

// Generate time options from 00:00 to 23:00
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

// Check if two ranges overlap
const rangesOverlap = (range1, range2) => {
  const start1 = parseInt(range1.start.replace(':', ''));
  const end1 = parseInt(range1.end.replace(':', ''));
  const start2 = parseInt(range2.start.replace(':', ''));
  const end2 = parseInt(range2.end.replace(':', ''));
  return start1 < end2 && start2 < end1;
};

// Validate a single range
const isValidRange = (range) => {
  const start = parseInt(range.start.replace(':', ''));
  const end = parseInt(range.end.replace(':', ''));
  return end > start;
};

// Time Block Component with dropdowns
const TimeBlock = ({ range, index, dayKey, onUpdate, onRemove, otherRanges }) => {
  const [error, setError] = useState(null);

  // Get available start hours (exclude hours that would cause overlap)
  const availableStartHours = useMemo(() => {
    return ALL_HOURS.filter(hour => {
      // Check if this start time would cause issues
      const testRange = { start: hour.value, end: range.end };
      if (!isValidRange(testRange)) return true; // Allow invalid for now, will show error
      
      // Check overlap with other ranges
      const hasOverlap = otherRanges.some(other => rangesOverlap(testRange, other));
      return !hasOverlap;
    });
  }, [range.end, otherRanges]);

  // Get available end hours (must be after start, no overlap)
  const availableEndHours = useMemo(() => {
    const startInt = parseInt(range.start.replace(':', ''));
    return ALL_HOURS.filter(hour => {
      const hourInt = parseInt(hour.value.replace(':', ''));
      if (hourInt <= startInt) return false; // Must be after start
      
      // Check overlap with other ranges
      const testRange = { start: range.start, end: hour.value };
      const hasOverlap = otherRanges.some(other => rangesOverlap(testRange, other));
      return !hasOverlap;
    });
  }, [range.start, otherRanges]);

  // Validate on change
  useEffect(() => {
    if (!isValidRange(range)) {
      setError("La hora de fin debe ser después de la hora de inicio");
    } else {
      const hasOverlap = otherRanges.some(other => rangesOverlap(range, other));
      if (hasOverlap) {
        setError("Este horario se solapa con otro bloque");
      } else {
        setError(null);
      }
    }
  }, [range, otherRanges]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Select
            value={range.start}
            onValueChange={(value) => onUpdate(dayKey, index, "start", value)}
          >
            <SelectTrigger className="w-28" data-testid={`${dayKey}-start-${index}`}>
              <SelectValue placeholder="Inicio" />
            </SelectTrigger>
            <SelectContent>
              {ALL_HOURS.map((hour) => (
                <SelectItem key={hour.value} value={hour.value}>
                  {hour.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <span className="text-stone-400 font-medium">a</span>
          
          <Select
            value={range.end}
            onValueChange={(value) => onUpdate(dayKey, index, "end", value)}
          >
            <SelectTrigger className="w-28" data-testid={`${dayKey}-end-${index}`}>
              <SelectValue placeholder="Fin" />
            </SelectTrigger>
            <SelectContent>
              {ALL_HOURS.map((hour) => (
                <SelectItem 
                  key={hour.value} 
                  value={hour.value}
                  disabled={parseInt(hour.value.replace(':', '')) <= parseInt(range.start.replace(':', ''))}
                >
                  {hour.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => onRemove(dayKey, index)}
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          data-testid={`remove-range-${dayKey}-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      {error && (
        <div className="flex items-center gap-1.5 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
    </div>
  );
};

export default function ListenerAvailability() {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [selectedExceptionDate, setSelectedExceptionDate] = useState(null);
  const [exceptionType, setExceptionType] = useState("blocked");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/listener/availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (dayKey) => {
    setAvailability(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [dayKey]: {
          ...prev.weekly[dayKey],
          enabled: !prev.weekly[dayKey].enabled
        }
      }
    }));
  };

  const addTimeRange = (dayKey) => {
    const existingRanges = availability.weekly[dayKey]?.ranges || [];
    
    // Find the next available time slot
    let defaultStart = "09:00";
    let defaultEnd = "17:00";
    
    if (existingRanges.length > 0) {
      // Find the latest end time and start from there
      const latestEnd = existingRanges.reduce((max, r) => {
        const end = parseInt(r.end.replace(':', ''));
        return end > max ? end : max;
      }, 0);
      
      if (latestEnd < 23) {
        defaultStart = `${latestEnd.toString().padStart(2, '0')}:00`;
        defaultEnd = `${Math.min(latestEnd + 2, 23).toString().padStart(2, '0')}:00`;
      }
    }
    
    setAvailability(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [dayKey]: {
          ...prev.weekly[dayKey],
          ranges: [...existingRanges, { start: defaultStart, end: defaultEnd }]
        }
      }
    }));
  };

  const updateTimeRange = (dayKey, index, field, value) => {
    setAvailability(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [dayKey]: {
          ...prev.weekly[dayKey],
          ranges: prev.weekly[dayKey].ranges.map((r, i) => 
            i === index ? { ...r, [field]: value } : r
          )
        }
      }
    }));
  };

  const removeTimeRange = (dayKey, index) => {
    setAvailability(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [dayKey]: {
          ...prev.weekly[dayKey],
          ranges: prev.weekly[dayKey].ranges.filter((_, i) => i !== index)
        }
      }
    }));
  };

  // Validate all ranges before saving
  const validateAvailability = () => {
    const errors = {};
    let hasErrors = false;
    
    DAYS.forEach(day => {
      const dayData = availability?.weekly?.[day.key];
      if (dayData?.enabled && dayData.ranges?.length > 0) {
        // Check each range
        dayData.ranges.forEach((range, idx) => {
          if (!isValidRange(range)) {
            errors[`${day.key}-${idx}`] = "Horario inválido";
            hasErrors = true;
          }
          
          // Check for overlaps with other ranges
          const otherRanges = dayData.ranges.filter((_, i) => i !== idx);
          const hasOverlap = otherRanges.some(other => rangesOverlap(range, other));
          if (hasOverlap) {
            errors[`${day.key}-${idx}`] = "Solapamiento detectado";
            hasErrors = true;
          }
        });
      }
    });
    
    setValidationErrors(errors);
    return !hasErrors;
  };

  const saveAvailability = async () => {
    if (!validateAvailability()) {
      toast.error("Corrige los errores antes de guardar");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/listener/availability`, availability.weekly);
      toast.success("Disponibilidad guardada");
    } catch (error) {
      console.error("Error saving availability:", error);
      toast.error("Error al guardar la disponibilidad");
    } finally {
      setSaving(false);
    }
  };

  const addException = async () => {
    if (!selectedExceptionDate) return;
    
    try {
      await axios.post(`${API}/listener/availability/exception`, {
        date: format(selectedExceptionDate, "yyyy-MM-dd"),
        blocked: exceptionType === "blocked",
        ranges: exceptionType === "blocked" ? null : []
      });
      await fetchAvailability();
      setShowExceptionDialog(false);
      setSelectedExceptionDate(null);
      toast.success("Excepción añadida");
    } catch (error) {
      console.error("Error adding exception:", error);
      toast.error("Error al añadir la excepción");
    }
  };

  const removeException = async (date) => {
    try {
      await axios.delete(`${API}/listener/availability/exception/${date}`);
      await fetchAvailability();
      toast.success("Excepción eliminada");
    } catch (error) {
      console.error("Error removing exception:", error);
      toast.error("Error al eliminar la excepción");
    }
  };

  if (loading) {
    return (
      <ListenerLayout title="Disponibilidad">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout title="Disponibilidad">
      <div className="space-y-8">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Cómo configurar tu disponibilidad</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Activa los días que quieras trabajar</li>
            <li>• Agrega uno o más bloques de horario por día</li>
            <li>• Puedes tener múltiples bloques (ej: 9:00-13:00 y 15:00-18:00)</li>
            <li>• Los horarios no pueden solaparse</li>
          </ul>
        </div>

        {/* Weekly Availability */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Disponibilidad semanal</h2>
          <p className="text-sm text-stone-500 mb-6">Define tus horarios recurrentes para cada día de la semana.</p>
          
          <div className="space-y-6">
            {DAYS.map((day) => {
              const dayData = availability?.weekly?.[day.key];
              const ranges = dayData?.ranges || [];
              
              return (
                <div key={day.key} className="border-b border-stone-100 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={dayData?.enabled || false}
                        onCheckedChange={() => handleDayToggle(day.key)}
                        data-testid={`toggle-${day.key}`}
                      />
                      <span className="font-medium text-foreground">{day.label}</span>
                    </div>
                    {dayData?.enabled && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addTimeRange(day.key)}
                        data-testid={`add-range-${day.key}`}
                        className="text-primary border-primary hover:bg-primary/5"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Añadir bloque
                      </Button>
                    )}
                  </div>
                  
                  {dayData?.enabled && (
                    <div className="ml-12 space-y-3">
                      {ranges.length === 0 ? (
                        <p className="text-sm text-stone-400 italic">
                          Sin horarios definidos. Añade un bloque de tiempo.
                        </p>
                      ) : (
                        ranges.map((range, idx) => (
                          <TimeBlock
                            key={idx}
                            range={range}
                            index={idx}
                            dayKey={day.key}
                            onUpdate={updateTimeRange}
                            onRemove={removeTimeRange}
                            otherRanges={ranges.filter((_, i) => i !== idx)}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200 flex justify-end">
            <Button 
              onClick={saveAvailability} 
              className="btn-primary" 
              disabled={saving} 
              data-testid="save-availability-btn"
            >
              {saving ? "Guardando..." : "Guardar disponibilidad"}
            </Button>
          </div>
        </div>

        {/* Exceptions */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Excepciones</h2>
              <p className="text-sm text-stone-500">Bloquea días específicos (vacaciones, citas, etc.)</p>
            </div>
            <Button variant="outline" onClick={() => setShowExceptionDialog(true)} data-testid="add-exception-btn">
              <Plus className="w-4 h-4 mr-2" />
              Añadir excepción
            </Button>
          </div>

          {(availability?.exceptions || []).length === 0 ? (
            <p className="text-stone-400 text-center py-8">Sin excepciones configuradas</p>
          ) : (
            <div className="space-y-2">
              {availability.exceptions.map((exception) => (
                <div key={exception.date} className="flex items-center justify-between bg-stone-50 rounded-xl px-4 py-3">
                  <div>
                    <span className="font-medium text-foreground">
                      {format(new Date(exception.date + 'T12:00:00'), "EEEE d 'de' MMMM", { locale: es })}
                    </span>
                    <span className={`ml-2 text-sm ${exception.blocked ? 'text-red-600' : 'text-amber-600'}`}>
                      {exception.blocked ? '(Bloqueado)' : '(Modificado)'}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => removeException(exception.date)}
                    className="text-stone-400 hover:text-red-500"
                    data-testid={`remove-exception-${exception.date}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Exception Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir excepción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Selecciona una fecha</Label>
              <Calendar
                mode="single"
                selected={selectedExceptionDate}
                onSelect={setSelectedExceptionDate}
                disabled={(date) => date < new Date()}
                className="rounded-xl border border-stone-200"
                locale={es}
                data-testid="exception-calendar"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Tipo de excepción</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exceptionType"
                    value="blocked"
                    checked={exceptionType === "blocked"}
                    onChange={() => setExceptionType("blocked")}
                    className="text-primary"
                  />
                  <span>Bloquear día completo</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowExceptionDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={addException} className="btn-primary" disabled={!selectedExceptionDate} data-testid="confirm-exception-btn">
                Añadir excepción
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ListenerLayout>
  );
}
